import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { Form, Input, Button, message, Spin, Divider, Modal } from 'antd-v5';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import { SettingsContent } from '../../components/SettingsContent';
import AudienceTagSelector from '../components/AudienceTagSelector';
import EventSessionForm from '../components/EventSessionForm';
import {
  useEvent,
  useEventSessions,
  useEventTags,
  useCreateEvent,
  useUpdateEvent,
  useReplaceEventTags,
  useArchiveEvent,
  useCreateSession,
  useUpdateSession,
  useArchiveSession,
  useReplaceSessionStores,
} from '../../../../../hooks/query/useEvents';
import './styles.scss';

const AddEditEvent = () => {
  const history = useHistory();
  const { eventId } = useParams();
  const { events: eventsFlag } = useFlags();

  // Redirect if feature flag is disabled
  if (!eventsFlag) {
    return <Redirect to="/" />;
  }

  const [form] = Form.useForm();
  const isEditMode = !!eventId;

  // Local state for sessions - start empty (sessions are optional)
  const [sessions, setSessions] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [audienceCount, setAudienceCount] = useState(0);
  const [sessionErrors, setSessionErrors] = useState({});
  const originalSessionsRef = useRef([]);

  // Fetch event data if editing
  const { data: eventData, isLoading: isLoadingEvent } = useEvent({
    eventId,
    useQueryOptions: { enabled: isEditMode },
  });

  const { data: eventSessions, isLoading: isLoadingSessions } = useEventSessions({
    eventId,
    useQueryOptions: { enabled: isEditMode },
  });

  const { data: eventTags, isLoading: isLoadingTags } = useEventTags({
    eventId,
    useQueryOptions: { enabled: isEditMode },
  });

  // Mutations
  const createEventMutation = useCreateEvent();
  const updateEventMutation = useUpdateEvent();
  const replaceTagsMutation = useReplaceEventTags();
  const archiveEventMutation = useArchiveEvent();
  const createSessionMutation = useCreateSession();
  const updateSessionMutation = useUpdateSession();
  const archiveSessionMutation = useArchiveSession();
  const replaceSessionStoresMutation = useReplaceSessionStores();

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && eventData) {
      form.setFieldsValue({
        name: eventData.name,
        description: eventData.description,
      });
    }
  }, [eventData, isEditMode, form]);

  // Populate sessions when editing
  useEffect(() => {
    // API returns sessions as direct array, not wrapped in .data
    const sessionsArray = eventSessions?.data || eventSessions;
    if (isEditMode && Array.isArray(sessionsArray) && sessionsArray.length > 0) {
      const mapped = sessionsArray.map(session => ({
        id: session.id,
        date: session.sessionDate || session.date, // API returns sessionDate
        startTime: session.startTime || session.start_time,
        endTime: session.endTime || session.end_time,
        storeIds: session.storeIds || session.stores?.map(s => s.id) || [], // API returns storeIds directly
      }));
      setSessions(mapped);
      originalSessionsRef.current = mapped.map(s => ({ ...s, storeIds: [...s.storeIds] }));
    }
  }, [eventSessions, isEditMode]);

  // Populate tags when editing
  useEffect(() => {
    if (isEditMode && eventTags?.data?.length > 0) {
      setSelectedTagIds(eventTags.data.map(tag => tag.id));
    }
  }, [eventTags, isEditMode]);

  const isLoading = isLoadingEvent || isLoadingSessions || isLoadingTags;
  const isSaving =
    createEventMutation.isLoading ||
    updateEventMutation.isLoading ||
    createSessionMutation.isLoading ||
    updateSessionMutation.isLoading ||
    archiveSessionMutation.isLoading ||
    replaceSessionStoresMutation.isLoading;

  const handleAddSession = () => {
    setSessions(prev => [
      ...prev,
      { id: `temp-${Date.now()}`, date: null, startTime: null, endTime: null, storeIds: [] },
    ]);
  };

  const handleUpdateSession = (index, updatedSession) => {
    setSessions(prev => {
      const newSessions = [...prev];
      newSessions[index] = { ...newSessions[index], ...updatedSession };
      return newSessions;
    });

    // Clear errors for updated fields
    if (sessionErrors[index]) {
      setSessionErrors(prev => {
        const newErrors = { ...prev };
        const sessionError = { ...newErrors[index] };

        Object.keys(updatedSession).forEach(key => {
          if (updatedSession[key] !== null && updatedSession[key] !== undefined) {
            if (key === 'storeIds' && updatedSession[key].length > 0) {
              delete sessionError[key];
            } else if (key !== 'storeIds') {
              delete sessionError[key];
            }
          }
        });

        if (Object.keys(sessionError).length === 0) {
          delete newErrors[index];
        } else {
          newErrors[index] = sessionError;
        }

        return newErrors;
      });
    }
  };

  const handleRemoveSession = index => {
    setSessions(prev => prev.filter((_, i) => i !== index));
  };

  const validateSession = session => {
    const errors = {};
    if (!session.date) errors.date = true;
    if (!session.startTime) errors.startTime = true;
    if (!session.endTime) errors.endTime = true;
    if (!session.storeIds || session.storeIds.length === 0) errors.storeIds = true;
    return errors;
  };

  const validateAllSessions = () => {
    const errors = {};
    let hasErrors = false;

    sessions.forEach((session, index) => {
      const sessionValidationErrors = validateSession(session);
      if (Object.keys(sessionValidationErrors).length > 0) {
        errors[index] = sessionValidationErrors;
        hasErrors = true;
      }
    });

    setSessionErrors(errors);
    return !hasErrors;
  };

  const handleCancel = () => {
    if (isEditMode) {
      history.push(`/Events/event-details/${eventId}`);
    } else {
      history.push('/Events');
    }
  };

  const handleArchive = () => {
    Modal.confirm({
      title: 'Archive Event',
      content: 'Are you sure you want to permanently archive this event?',
      okText: 'Archive',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await archiveEventMutation.mutateAsync(eventId);
          message.success('Event archived successfully');
          history.push('/Events');
        } catch (error) {
          console.error('Error archiving event:', error);
          message.error('Failed to archive event. Please try again.');
        }
      },
    });
  };

  const persistSessionChanges = async () => {
    const originals = originalSessionsRef.current;
    const currentIds = new Set(sessions.map(s => s.id));
    const originalIds = new Set(originals.map(s => s.id));

    // Normalize time to HH:MM for comparison
    const normalizeTime = t => (t ? t.slice(0, 5) : '');

    // Sessions removed (in original but not in current)
    const removedSessions = originals.filter(s => !currentIds.has(s.id));

    // Sessions added (temp IDs)
    const addedSessions = sessions.filter(s => String(s.id).startsWith('temp-'));

    // Sessions that may have been updated (exist in both)
    const potentiallyUpdated = sessions.filter(s => originalIds.has(s.id));

    const archivePromises = removedSessions.map(s =>
      archiveSessionMutation.mutateAsync({ eventId, sessionId: s.id }),
    );

    const updatePromises = [];
    const storeUpdatePromises = [];

    potentiallyUpdated.forEach(current => {
      const original = originals.find(o => o.id === current.id);
      if (!original) return;

      // Check date/time changes
      const changes = {};
      if (current.date !== original.date) changes.sessionDate = current.date;
      if (normalizeTime(current.startTime) !== normalizeTime(original.startTime))
        changes.startTime = current.startTime;
      if (normalizeTime(current.endTime) !== normalizeTime(original.endTime)) changes.endTime = current.endTime;

      if (Object.keys(changes).length > 0) {
        updatePromises.push(
          updateSessionMutation.mutateAsync({ eventId, sessionId: current.id, data: changes }),
        );
      }

      // Check store changes using String() comparison
      const currentStoresSorted = [...(current.storeIds || [])].map(String).sort();
      const originalStoresSorted = [...(original.storeIds || [])].map(String).sort();
      const storesChanged =
        currentStoresSorted.length !== originalStoresSorted.length ||
        currentStoresSorted.some((id, i) => id !== originalStoresSorted[i]);

      if (storesChanged) {
        storeUpdatePromises.push(
          replaceSessionStoresMutation.mutateAsync({
            eventId,
            sessionId: current.id,
            storeIds: current.storeIds || [],
          }),
        );
      }
    });

    // New sessions: create then assign stores
    const createPromises = addedSessions.map(async s => {
      const result = await createSessionMutation.mutateAsync({
        eventId,
        data: {
          sessionDate: s.date,
          startTime: s.startTime,
          endTime: s.endTime,
        },
      });
      const newSessionId = result?.id || result?.data?.id;
      if (newSessionId && s.storeIds?.length > 0) {
        await replaceSessionStoresMutation.mutateAsync({
          eventId,
          sessionId: newSessionId,
          storeIds: s.storeIds,
        });
      }
    });

    await Promise.all([...archivePromises, ...updatePromises, ...storeUpdatePromises, ...createPromises]);
  };

  const handleSubmit = async values => {
    try {
      // Validate all sessions first
      if (sessions.length === 0) {
        message.error('Please add at least one event date and time');
        return;
      }

      // Check if all sessions are complete
      if (!validateAllSessions()) {
        message.error('Please complete all session fields (Date, Start Time, End Time, and Store)');
        return;
      }

      // Validate at least one tag is required for audience
      if (selectedTagIds.length === 0) {
        message.error('Please select at least one tag for the audience');
        return;
      }

      const eventPayload = {
        name: values.name,
        description: values.description || '',
        sessions: sessions.map(s => ({
          sessionDate: s.date,
          startTime: s.startTime,
          endTime: s.endTime,
          storeIds: s.storeIds || [],
        })),
      };

      let savedEventId = eventId;

      if (isEditMode) {
        await updateEventMutation.mutateAsync({
          eventId,
          data: { name: values.name, description: values.description },
        });
        await persistSessionChanges();
      } else {
        const result = await createEventMutation.mutateAsync(eventPayload);
        savedEventId = result?.id || result?.data?.id;
      }

      // Update tags if we have a saved event ID (always call to allow removing all tags)
      if (savedEventId) {
        await replaceTagsMutation.mutateAsync({
          eventId: savedEventId,
          tagIds: selectedTagIds,
        });
      }

      message.success(isEditMode ? 'Event updated successfully' : 'Event created successfully');
      if (isEditMode) {
        history.push(`/Events/event-details/${eventId}`);
      } else {
        history.push('/Events');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      message.error('Failed to save event. Please try again.');
    }
  };

  const breadcrumb = [{ path: '/Events', title: 'Events' }, { title: isEditMode ? 'Edit Event' : 'New Event' }];

  // Header actions for StandardLayoutV2
  const headerActions = (
    <div className="add-edit-event-header-actions">
      {isEditMode && (
        <Button danger onClick={handleArchive} loading={archiveEventMutation.isLoading}>
          Archive
        </Button>
      )}
      <Button type="primary" onClick={() => form.submit()} loading={isSaving}>
        Save
      </Button>
      <Button onClick={handleCancel}>Cancel</Button>
    </div>
  );

  if (isEditMode && isLoading) {
    return (
      <StandardLayoutV2
        title={isEditMode ? 'Edit Event' : 'New Event'}
        breadcrumb={breadcrumb}
        classProps="flex-col-left"
        maxWidth="920px"
        styleProps={{ justifyContent: 'flex-start' }}
        containerStyles={{ backgroundColor: 'transparent', paddingRight: '32px', width: '100%' }}
      >
        <div className="loading-container">
          <Spin size="large" />
        </div>
      </StandardLayoutV2>
    );
  }

  return (
    <StandardLayoutV2
      title={isEditMode ? 'Edit Event' : 'New Event'}
      breadcrumb={breadcrumb}
      classProps="flex-col-left"
      maxWidth="920px"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingRight: '32px', width: '100%' }}
      actions={headerActions}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ width: '100%' }}>
        {/* Event Details Section */}
        <SettingsContent
          icon={<FontAwesomeIcon icon={['far', 'calendar-star']} />}
          title="Event Details"
          description="Give your event a name to help identify it"
        >
          <Form.Item
            name="name"
            label="Event Name"
            rules={[{ required: true, message: 'Please enter an event name' }]}
            style={{ marginBottom: 0, width: 550 }}
          >
            <Input placeholder="Enter an Event Name" maxLength={60} showCount style={{ width: '100%' }} />
          </Form.Item>
        </SettingsContent>

        <Divider style={{ margin: '40px 0' }} />

        {/* Audience Section */}
        <SettingsContent
          icon={<FontAwesomeIcon icon={['far', 'users']} />}
          title={
            <span className="audience-title-row">
              Audience <span className="audience-count">{audienceCount.toLocaleString()} clients</span>
            </span>
          }
          description="Identify the target audience for this event using tags. Each tag will add people to the audience."
        >
          <AudienceTagSelector
            selectedTagIds={selectedTagIds}
            onTagsChange={setSelectedTagIds}
            onCountChange={setAudienceCount}
          />
        </SettingsContent>

        <Divider style={{ margin: '40px 0' }} />

        {/* Event Dates and Times Section */}
        <SettingsContent
          icon={<FontAwesomeIcon icon={['far', 'calendar']} />}
          title="Event Dates and Times"
          description="Set up where and when clients can book appointments for this event."
        >
          {sessions.length > 0 &&
            sessions.map((session, index) => (
              <EventSessionForm
                key={session.id}
                session={session}
                index={index}
                onUpdate={data => handleUpdateSession(index, data)}
                onRemove={() => handleRemoveSession(index)}
                canRemove
                errors={sessionErrors[index] || {}}
              />
            ))}

          <Button
            type="dashed"
            onClick={handleAddSession}
            icon={<FontAwesomeIcon icon={['fal', 'plus']} />}
            className={sessions.length > 0 ? 'm-top-16' : ''}
          >
            Add Dates
          </Button>
        </SettingsContent>
      </Form>
    </StandardLayoutV2>
  );
};

export default AddEditEvent;
